import React, { useState, useEffect, Suspense, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import { storeCognitoId } from '../services/api'
import OnStateChangeContext from '../Contexts/OnStateChangeContext'
import AuthLayout from '../Components/AuthLayout'
import LoadingPage from '../Components/LoadingPage'
import { trackRegistration } from '../Global/Analytics'
import {
  checkPaperlessStatus,
  getCookie,
  getQueryParams,
  setCookie,
} from '../Utils/utils'
import { useAuth0 } from '@auth0/auth0-react'
import { useApiContext } from '../Hooks/useApiContext'
import {
  isBrowser,
  readStorageBoolean,
  readStorageJSON,
  readStorageNumber,
  writeStorage,
} from '../Utils/storage'

// Date-based rules are read lazily so they reflect the current day and storage state
const getCurrentDate = () => new Date().getDate()
// TODO remove after testing
const getMfaCutoffDate = () => readStorageNumber('MFA_CUTOFF_DATE', 9) // The cutoff date for showing MFA to all users

const ConfirmPhone = React.lazy(() =>
  import('../Components/auth/ConfirmPhone'),
)
const AddPhone = React.lazy(() =>
  import('../Components/auth/AddPhone'),
)

const SignUpLayout = React.lazy(() =>
  import('../Components/SignUpLayout'),
)

const FinishYourAccountContainer = React.lazy(() =>
  import('../Containers/FinishYourAccountContainer'),
)
const BadRequestPage = React.lazy(() =>
  import('../Components/BadRequestPage'),
)

export const checkIsRedirectAllowed = (user, loanLoading) => {
  // Without browser storage we can't verify onboarding state, so don't allow redirects
  if (!isBrowser()) {
    return false
  }

  // Otherwise, check if the redirectAllowed flag is set in localStorage
  const stateRedirectAllowed = readStorageJSON('redirectAllowed', null)
  const paperlessIndicator = checkPaperlessStatus()

  if (loanLoading || loanLoading === null) {
    return true
  }

  if (stateRedirectAllowed) {
    return true
  }

  // if (user?.first_login === 'true' || user?.logins_count < 2) {
  //   return user?.first_login === 'true' || user?.logins_count < 2
  //     ? false
  //     : true
  // }

  // Parse the localStorage value properly - it could be "true" or "false" as strings
  if (user?.mfa && paperlessIndicator) {
    return true
  }

  // If: User declines opting into paperless or mfa, and it is the 1st-9th of the month
  // By default, don't allow redirects if we're before/on the cutoff date
  return getCurrentDate() > getMfaCutoffDate();
}

const AuthRouter = ({
  children,
  isAuthenticated,
  setUserInfo,
  setIsAuthenticated,
  authUser,
}) => {
  const location = useLocation()
  const [authState, setAuthState] = useState(() => {
    // First, check if user is authenticated at all
    if (!isAuthenticated.isAuthenticated) {
      // If not authenticated but on registration path
      if (location.pathname.includes('register')) {
        return 'signUp'
      }
      // Not authenticated, default to sign in
      return 'signIn'
    }

    // User is authenticated at this point

    // We'll set the authState in the useEffect only on login event
    // This prevents automatic redirects on page refresh
    // The isLoginEvent flag will control when to show finish-your-account
    if (!checkIsRedirectAllowed(authUser, null)) {
      return 'confirmPhoneAndMFA'
    }

    // If authenticated (with or without MFA), go to signed in state
    return 'signedIn'
  })
  const [authData, setAuthData] = useState({
    user: authUser,
    redirect: authUser ? true : false,
  })
  // isAuthorized state is passed to child components but not directly used in conditions
  // eslint-disable-next-line no-unused-vars
  const [isAuthorized, setIsAuthorized] = useState(false)
  // const [skip, setSkip] = useState(false)
  const [skip, setSkip] = useState(() =>
    readStorageBoolean('skipFinishAccount', false),
  )

  // Add state for paperless opt-in check
  const [isPaperlessOptedIn, setIsPaperlessOptedIn] = useState(true)

  const navigate = useNavigate()
  const client = useApolloClient()
  const { pathname } = useLocation()
  const { loginWithRedirect } = useAuth0()
  const { loan } = useApiContext()
  const { loading: loanLoading } = loan ?? {}

  // Memoize the redirectAllowed value to prevent recalculation on every render
  const redirectAllowed = useMemo(
    () =>
      !loanLoading
        ? checkIsRedirectAllowed(authUser, loanLoading)
        : false,
    // Recompute on auth user and loan state changes; pathname is included so the
    // value is re-read from storage after the finish-your-account flow navigates away
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authUser, loanLoading, pathname],
  )

  const handleSignup = () => {
    trackRegistration.Started()
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
      },
    }).catch((err) => console.error('Signup error:', err))
  }

  useEffect(() => {
    const signup = getQueryParams('signup')
    if (signup === '1') {
      handleSignup()
    }

    const code = getQueryParams('code')
    const state = getQueryParams('state')
    const iss = getQueryParams('iss')
    if (code || state || iss) {
      // Clear the signup param from URL to prevent repeated triggers
      const url = new URL(window.location)
      url.searchParams.delete('code')
      url.searchParams.delete('state')
      url.searchParams.delete('iss')
      window.history.replaceState({}, document.title, url.toString())
    }

    // handleSignup is defined in the component and won't change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Check if we should set redirect to true based solely on date being less than or equal to MFA_CUTOFF_DATE
    const shouldRedirect = authUser && getCurrentDate() <= getMfaCutoffDate()

    setAuthData({ user: authUser, redirect: shouldRedirect })

    // We only want this effect to run when authUser changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser])

  // This effect will only run when isAuthenticated changes
  // useEffect(() => {
  //   if (
  //     !isAuthenticated.isAuthenticated ||
  //     loanLoading === null ||
  //     loanLoading
  //   )
  //     return

  //   // If redirect is not allowed, show the finish-your-account screen
  //   if (!redirectAllowed && isAuthenticated.isAuthenticated) {
  //     setAuthState('confirmPhoneAndMFA')
  //     history.push('/finish-your-account')
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isAuthenticated.isAuthenticated, loanLoading])

  // Only declare navigate once at the top of the component
  useEffect(() => {
    if (
      !isAuthenticated.isAuthenticated ||
      loanLoading === null ||
      loanLoading ||
      skip
    )
      return;

    if (!redirectAllowed) {
      setAuthState('confirmPhoneAndMFA');
      navigate('/finish-your-account', { replace: true });
    }
  }, [isAuthenticated.isAuthenticated, loanLoading, skip, navigate, redirectAllowed]);


  // Handle signedIn state in a useEffect with controlled dependencies
  // useEffect(() => {
  //   if (!isAuthenticated.isAuthenticated) return
  //   // Only set the state to signedIn if redirectAllowed is true
  //   // This happens only once since redirectAllowed is memoized
  //    setSkip(true)
  // localStorage.setItem('skipFinishAccount', 'true')
  //   if (redirectAllowed && authState !== 'signedIn') {
  //     setAuthState('signedIn')
  //   }

  //   // This will handle the case where state is already signedIn but user data is missing
  //   if (authState === 'signedIn' && !authData.user && authUser) {
  //     const timer = setTimeout(() => {
  //       handleSignedIn(
  //         setAuthState,
  //         setIsAuthorized,
  //         history,
  //         skip,
  //         authUser,
  //         client,
  //       )
  //     }, 0)

  //     return () => clearTimeout(timer)
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [authState, authData.user, authUser])

useEffect(() => {
  if (!isAuthenticated.isAuthenticated) return

  if (!skip) {
    setSkip(true)
    writeStorage('skipFinishAccount', true)
  }

  if (redirectAllowed && authState !== 'signedIn') {
    setAuthState('signedIn')
  }
}, [isAuthenticated.isAuthenticated, redirectAllowed, authState, skip])



  // Check paperless status when component mounts or auth user changes
  useEffect(() => {
    if (authUser) {
      setIsPaperlessOptedIn(checkPaperlessStatus())
    }
  }, [authUser])

  // Debug panel for authentication state
  const debugPanel = (
    <details style={{background:'#fffbe6',border:'1px solid orange',padding:8,margin:8}} open>
      <summary>Debug Info (AuthRouter)</summary>
      <pre style={{fontSize:12,maxHeight:300,overflow:'auto'}}>{JSON.stringify({
        authState,
        authUser,
        isAuthenticated,
        redirectAllowed,
        skip,
        pathname,
      }, null, 2)}</pre>
    </details>
  );
  return (
    <OnStateChangeContext.Provider value={authState}>
      {debugPanel}
      {getPage(
        authState,
        setAuthState,
        authData,
        setAuthData,
        children,
        // onStateChange,
        isAuthenticated,
        setIsAuthorized,
        history,
        setUserInfo,
        setIsAuthenticated,
        authUser,
        setSkip,
        skip,
        pathname,
        client,
        isPaperlessOptedIn,
        redirectAllowed,
      )}
    </OnStateChangeContext.Provider>
  )
}

const getPage = (
  authState,
  setAuthState,
  authData,
  setAuthData,
  children,
  // onStateChange,
  isAuthorized,
  setIsAuthorized,
  history,
  setUserInfo,
  setIsAuthenticated,
  authUser,
  setSkip,
  skip,
  pathname,
  client,
  isPaperlessOptedIn,
  redirectAllowed,
) => {
  // Use the redirectAllowed value passed from the parent component instead of recalculating
  // to prevent infinite re-renders

  switch (authState) {
    case 'setup':
    case 'signIn': {
      return authUser ? (
        <LoadingPage />
      ) : (
        <AuthLayout
          setAuthState={setAuthState}
          setAuthData={setAuthData}
          setUserInfo={setUserInfo}
          setIsAuthenticated={setIsAuthenticated}
          authState={authState}
        />
      )
    }

    case 'signUp': {
      return (
        <Suspense fallback={<LoadingPage />}>
          {pathname !== '/register/failed' ? (
            <SignUpLayout>page notfound</SignUpLayout>
          ) : (
            <BadRequestPage error={'forbiddenEmail'} />
          )}
        </Suspense>
      )
    }

    case 'confirmPhoneAndMFA': {
      return (
        <Suspense fallback={<LoadingPage />}>
          <FinishYourAccountContainer
            setAuthState={setAuthState}
            setSkip={setSkip}
          />
        </Suspense>
      )
    }

    case 'confirmPhone': {
      return (
        <AuthLayout>
          <Suspense fallback={<LoadingPage />}>
            <ConfirmPhone
              authData={authData}
              setAuthState={setAuthState}
            />
          </Suspense>
        </AuthLayout>
      )
    }

    case 'addPhone': {
      return (
        <AuthLayout>
          <Suspense fallback={<LoadingPage />}>
            <AddPhone
              authData={authData}
              setAuthState={setAuthState}
            />
          </Suspense>
        </AuthLayout>
      )
    }

    case 'publicPage': {
      return <>{authData}</>
    }
    case 'signedIn': {
      // Check redirectAllowed from AuthRouter component instead of recalculating
      if (!redirectAllowed && !skip) {
        return (
          <Suspense fallback={<LoadingPage />}>
            <FinishYourAccountContainer />
          </Suspense>
        )
      }

      return (
        <Suspense fallback={<LoadingPage />}>
          {!authUser ? <LoadingPage /> : children}
        </Suspense>
      )
    }

    case 'loading':
    default: {
      return <React.Fragment />
    }
  }
}

const handleSignedIn = async (
  setAuthState,
  setIsAuthorized,
  history,
  skip,
  authUser,
  client,
) => {
  // First, ensure we have a valid user
  if (!authUser) {
    // If no authenticated user, set to signIn state
    setAuthState('signIn')
    return
  }

  // eslint-disable-next-line no-unused-vars
  const confirmSignUpCookie = getCookie('confirm_sign_up')
  const storeCognitoIdCookie = getCookie('store_cognito_id')

  if (
    !storeCognitoIdCookie &&
    authUser['custom:person_id'] &&
    authUser['cognito:identityId']
  ) {
    setCookie('store_cognito_id', 'true', 1)
    storeCognitoId(
      authUser['custom:person_id'],
      authUser['cognito:identityId'],
      client,
    )
  }

  if (!authUser['email_verified']) {
    setAuthState('confirmSignUp')
    return
  }

  // We don't need to check paperless status anymore with the simplified rule
  // but keeping this code commented for future reference
  /*
  const isPaperlessOptedIn = (() => {
    try {
      const userTraits = localStorage.getItem('ajs_user_traits')
      if (userTraits) {
        const parsedTraits = JSON.parse(userTraits)
        return parsedTraits?.paperless_opt_in === true
      }
      return true // Default to true if can't determine
    } catch (error) {
      console.error(
        'Error checking paperless status in handleSignedIn:',
        error,
      )
      return true // Default to true if error
    }
  })()
  */

  // We no longer need to calculate shouldShowFYA since we're using the memoized redirectAllowed
  // from the parent component to determine if we need to show the finish-your-account page

  // Only set redirectAllowed to true if we're past the MFA_CUTOFF_DATE

  // if (shouldShowFYA) {
  //   setCookie('confirm_sign_up', 'false', -1)

  //   // Do state update first, then navigation
  //   if (
  //     !VIEW_SCREENS.some((view) =>
  //       history.location.pathname.includes(view),
  //     )
  //   ) {
  //     setAuthState('confirmPhoneAndMFA')

  //     // Delay navigation to allow state update to complete
  //     setTimeout(() => {
  //       // Use replace instead of push to avoid duplicate history entries
  //       history.replace('/finish-your-account')
  //     }, 0)
  //   }

  //   return
  // }

  setIsAuthorized(true)
}

export default AuthRouter
